import { getUserInfo } from 'app/store/session/repository';
import React, { FC, useCallback } from 'react';
import { Dialog } from 'shared/components/Dialog';
import { DialogName } from 'app/enums';
import { Box, Button, Typography } from '@mui/material';
import { useStyles } from './UnlinkDialog.styles';
import { modalsReducers } from 'app/store/modal/slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  PlatformLinkingConnectionStatus,
  PlatformLinkingProvider,
} from '@okletsplay/core/src/modules/platform-linking/enums';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { platformLinkingSelectorFactory } from 'modules/platform-linking/store/selectors';
import linkImage from 'assets/images/app_link.svg';
import classNames from 'classnames';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { unlinkProvider } from 'modules/platform-linking/store/repository';
import { dispatchUnwrap } from 'app/utils/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { EntityStoreName } from 'app/enums/store';
import { PlatformLinkingStoreStatus } from 'modules/platform-linking/store/enums';
import { platformLinkingReducers } from 'modules/platform-linking/store/slice';

export const UnlinkDialog: FC = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const providerId: PlatformLinkingProvider = useSelector(
    modalsSelectorFactory.useModalPayload(DialogName.UnlinkPlatform)
  );
  const provider = useSelector(platformLinkingSelectorFactory.useById(providerId));
  const unlinking = useEntityRepositoryStatus({
    [EntityStoreName.PlatformLinking]: PlatformLinkingStoreStatus.Unlinking,
  });

  const closeModal = useCallback(() => {
    dispatch(modalsReducers.closeModal(DialogName.UnlinkPlatform));
  }, [dispatch]);

  const onUnlinkHandler = useCallback(() => {
    const platformId = provider?.platformId as PlatformLinkingProvider;

    if (!platformId) return;

    dispatchUnwrap(unlinkProvider(platformId), {
      success: () => {
        dispatch(getUserInfo());
        closeModal();
      },
      error: () => {
        dispatch(
          platformLinkingReducers.setLinkingConnectionStatus({
            platformId,
            connectionStatus: PlatformLinkingConnectionStatus.HasNotFinishedActivities,
          })
        );
        closeModal();
      },
    });
  }, [dispatch, closeModal, provider?.platformId]);

  return (
    <Dialog
      maxWidth="sm"
      contentHeight={300}
      name={DialogName.UnlinkPlatform}
      actions={
        <>
          <LoaderButton
            fullWidth
            size="medium"
            color="error"
            variant="outlined"
            className={classNames(classes.button, classes.unlink)}
            onClick={onUnlinkHandler}
            loading={unlinking}
          >
            Unlink
          </LoaderButton>
          <Button
            fullWidth
            size="medium"
            color="primary"
            variant="contained"
            className={classes.button}
            onClick={closeModal}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Box className={classes.root}>
        <Box className={classes.image}>
          <img src={linkImage} alt="link-image" />
        </Box>
        <Typography className={classes.title}>Unlink Platform?</Typography>
        <Typography>
          Are you sure you want to unlink your {provider?.platformName ?? ''} account?
        </Typography>
      </Box>
    </Dialog>
  );
};
